import React, { useRef, useState } from "react";
import { Box, IconButton, InputAdornment, Popover, TextField } from "@mui/material";
import {
  CalendarMonthRounded,
  ClearRounded,
} from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";
import { themedFormInputSx } from "../common/formFieldStyles";
import { useClinicTheme } from "../../theme";
import CalendarSurface, { formatDateShortTR } from "./CalendarSurface";

/**
 * FormDatePicker — form alanlarında kullanılan kompakt tarih seçici.
 *
 * Tetikleyici salt-okunur bir input; takvim `CalendarSurface` ile çizilir ve
 * **popover olarak içeriğin üstüne** açılır, alanları aşağı itmez. Uygulamadaki
 * geniş kart tetikleyicili `TrCalendarPicker` ile aynı takvimi kullanır.
 *
 * Değer "yyyy-MM-dd" string alır/verir (mevcut form state'leriyle uyumlu).
 *
 * @param {string} value                "yyyy-MM-dd" veya boş string
 * @param {(ymd: string) => void} onChange
 * @param {string} [label]              Input etiketi
 * @param {object} [palette]            Klinik tema paleti (verilmezse tema okunur)
 * @param {boolean} [disabled] @param {boolean} [fullWidth] @param {boolean} [clearable]
 * @param {string} [minDate] @param {string} [maxDate]   "yyyy-MM-dd"
 * @param {"outlined"|"standard"} [variant]  Detay sayfalarında `standard`
 *   kullanılır (InfoRow satırları); form/drawer'larda `outlined`.
 * @param {object} [sx]                 TextField kök sx override'ı
 */
export default function FormDatePicker({
  value,
  onChange,
  label,
  palette,
  disabled = false,
  fullWidth = false,
  clearable = true,
  size = "small",
  variant = "outlined",
  placeholder = "gg.aa.yyyy",
  minDate,
  maxDate,
  sx = {},
}) {
  const { palette: themePalette } = useClinicTheme();
  const primary = palette?.primary || themePalette.primary;
  const outlinedBase = themedFormInputSx(palette || themePalette);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleSelect = (ymd) => {
    onChange?.(ymd);
    setOpen(false);
  };

  return (
    <>
      <TextField
        ref={anchorRef}
        value={formatDateShortTR(value)}
        placeholder={placeholder}
        label={label}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        variant={variant}
        onClick={() => !disabled && setOpen(true)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true, style: { cursor: disabled ? "not-allowed" : "pointer" } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {clearable && value && !disabled ? (
                <IconButton
                  size="small"
                  aria-label="Tarihi temizle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.("");
                  }}
                >
                  <ClearRounded sx={{ fontSize: 16, color: COLORS.slate400 }} />
                </IconButton>
              ) : null}
              <CalendarMonthRounded sx={{ fontSize: 18, color: disabled ? COLORS.slate300 : primary }} />
            </InputAdornment>
          ),
        }}
        sx={{
          ...(variant === "outlined"
            ? {
                // FormTextField / SearchField ile birebir aynı kutu: yükseklik, kenarlık rengi/kalınlığı, odak
                ...outlinedBase,
                "& .MuiOutlinedInput-root": {
                  ...outlinedBase["& .MuiOutlinedInput-root"],
                  cursor: disabled ? "not-allowed" : "pointer",
                },
              }
            : {
                // Detay sayfası dili: alt çizgili, kompakt satır.
                "& .MuiInput-root": {
                  fontSize: "0.875rem",
                  cursor: disabled ? "not-allowed" : "pointer",
                  "&:after": { borderBottomColor: primary },
                },
              }),
          ...sx,
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 300,
              border: `1px solid ${COLORS.slate200}`,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
            },
          },
        }}
      >
        <Box>
          <CalendarSurface
            value={value}
            onChange={handleSelect}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Box>
      </Popover>
    </>
  );
}
